import { ReactNode, FC, useEffect, useRef, useMemo, useState } from 'react';
import { Dialog as HeadlessDialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react';
import { noop, throttle } from 'lodash-es';
import cx from 'classnames';
import { CloseIcon } from '@/components/base/Icons/RocheIconPack';
import IconButton from '../IconButton/IconButton';
import ScrollArea from '../ScrollArea/ScrollArea';
import Text from '../Text/Text';
import styles from './Dialog.module.css';

export const DIALOG_SIZE = {
  S: 'S',
  M: 'M',
  L: 'L',
  XL: 'XL',
} as const;

const SCROLL_THROTTLE_MS = 100;

export type DialogSize = keyof typeof DIALOG_SIZE;

export interface DialogProps {
  'isOpen': boolean;
  'onClose': VoidFunction;
  'title': ReactNode;
  'description'?: string;
  'children': ReactNode;
  'size'?: DialogSize;
  'footer'?: ReactNode;
  'fixedContent'?: ReactNode;
  'className'?: string;
  'separatorClassName'?: string;
  'fixedContentClassName'?: string;
  'scrollableContentClassName'?: string;
  'preventDismiss'?: boolean;
  'data-testid'?: string;
}

const Dialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'M',
  footer,
  fixedContent,
  className,
  separatorClassName,
  fixedContentClassName,
  scrollableContentClassName,
  preventDismiss,
  'data-testid': dataTestId = 'dialog',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setIsDescriptionVisible(true);
      lastScrollTop.current = 0;
    }
  }, [isOpen]);

  const handleScroll = useMemo(
    () =>
      throttle(
        () => {
          const el = scrollRef.current;
          if (!el || !description) return;

          const currentScrollTop = el.scrollTop;
          const isScrollingDown = currentScrollTop > lastScrollTop.current;

          if (isScrollingDown && currentScrollTop > 0) {
            setIsDescriptionVisible(false);
          } else {
            setIsDescriptionVisible(true);
          }

          lastScrollTop.current = currentScrollTop;
        },
        SCROLL_THROTTLE_MS,
        { leading: true, trailing: true },
      ),
    [description],
  );

  useEffect(() => {
    return () => {
      handleScroll.cancel();
    };
  }, [handleScroll]);

  return (
    <HeadlessDialog open={isOpen} onClose={preventDismiss ? noop : onClose} aria-labelledby={`${dataTestId}-title`}>
      <DialogBackdrop className={styles.backdrop} data-testid={`${dataTestId}-backdrop`} />

      <div className={styles.dialogWrapper}>
        <DialogPanel className={cx(styles.dialog, className)} data-size={size} data-testid={dataTestId}>
          <header className={styles.header}>
            <DialogTitle as="h2" className={styles.title} id={`${dataTestId}-title`}>
              {title}
            </DialogTitle>
            <IconButton
              appearance="text"
              theme="light"
              size="M"
              onClick={onClose}
              icon={<CloseIcon />}
              aria-label="Close dialog"
            />
          </header>

          {description && (
            <div
              className={cx(styles.description, {
                [styles.descriptionHidden]: !isDescriptionVisible,
              })}
              data-testid={`${dataTestId}-description`}
            >
              <Text as="p" className={styles.descriptionText}>
                {description}
              </Text>
            </div>
          )}

          {fixedContent && (
            <>
              <div className={cx(styles.fixedContent, fixedContentClassName)}>{fixedContent}</div>
              <div className={cx(styles.separator, separatorClassName)} />
            </>
          )}

          <ScrollArea
            scrollbars="y"
            className={styles.scrollableContent}
            viewportRef={scrollRef}
            viewportProps={{ onScroll: handleScroll }}
            classNames={{ viewport: cx(styles.scrollableViewport, scrollableContentClassName) }}
          >
            <div data-testid={`${dataTestId}-content`}>{children}</div>
          </ScrollArea>

          {footer && (
            <div className={styles.footer} data-testid={`${dataTestId}-footer`}>
              {footer}
            </div>
          )}
        </DialogPanel>
      </div>
    </HeadlessDialog>
  );
};

export default Dialog;
